import { apiRequest } from '../client'
import type { AffectedOrder } from './inventory-count-sessions'

export type { AffectedOrder }

export type DeliveryStatus = 'ANNOUNCED' | 'BOOKED' | 'CANCELLED'

/**
 * The list endpoint additionally accepts the pseudo-status `OVERDUE` ("ANNOUNCED and past its
 * expected day"), which is derived server-side in the store's timezone and narrows only when it
 * is the sole status filter.
 */
export type DeliveryListStatus = DeliveryStatus | 'OVERDUE'

/**
 * How a delivery got into paydesk. `MANUAL` is a hand-announced document; anything else names the
 * supplier system that imported it (CHG-2026-09-webbill-delivery-import).
 */
export type DeliverySource = 'MANUAL' | 'WEBBILL' | 'ORDER' | (string & {})

export interface DeliveryLine {
  sku: string
  /** Product name resolved by the backend; null for a SKU that lost its product. */
  name: string | null
  /** Always in BASE units (Stück), whatever package unit the delivery note stated. */
  expectedQty: number
  /** null until the delivery is booked (and on a cancelled document). */
  receivedQty: number | null
  note: string | null
  /** CHG-2026-09-stock-usage-and-price-twins: the article's base unit as shown ("Stk" by default). */
  unit?: string
  /** "Teilmengen zählen": the quantities of this line may carry up to 3 decimals (phase B4). */
  fractionalCounting?: boolean
  /**
   * CHG-2026-09-stock-usage-and-price-twins (D11): set only on a line whose product holds no
   * stock; booking keeps the line but books nothing for it. Quantities are decimal numbers.
   */
  noStock?: 'dish' | 'variant' | 'not_tracked'
  /**
   * The delivered package unit and its net purchase price, as the supplier's delivery note stated
   * them. All null on a manually announced line.
   *
   * Descriptive only: the quantities above stay in base units. `contentPerUnit` is frozen at
   * import time, and `unitLevel` is null on a line whose unit could not be resolved — that line
   * was imported with factor 1 and needs a human before booking.
   */
  unitLevel: number | null
  unitName: string | null
  unitCount: number | null
  contentPerUnit: number | null
  /** Net price per DELIVERED unit (per Karton, not per Stück). */
  unitCostCents: number | null
  vatRate: string | null
  /** Derived server-side: per-piece price × received (or expected, before booking). */
  lineValueCents: number | null
}

/** One row of the list endpoint — the header without its lines. */
export interface DeliveryListItem {
  id: string
  restaurantId: number
  supplier: string | null
  deliveryNoteRef: string | null
  /** ISO date (yyyy-MM-dd), no time of day by design. */
  expectedDate: string
  countsForPreorders: boolean
  status: DeliveryStatus
  source: DeliverySource
  /**
   * Derived server-side (ANNOUNCED and expected date before today in store time), never stored,
   * so the client must not recompute it from `expectedDate` in the browser's timezone.
   */
  overdue: boolean
  lineCount: number
  note: string | null
  /** The purchase order that announced this delivery (source `ORDER`, or converted from it); else null. */
  purchaseOrderId?: string | null
  createdBy: string | null
  createdAt: string | null
  bookedBy: string | null
  bookedAt: string | null
  cancelledBy: string | null
  cancelledAt: string | null
}

export interface DeliveryDetail extends DeliveryListItem {
  lines: Array<DeliveryLine>
  /** Sum of the lines' values; null when no position carries a purchase price. */
  goodsValueNetCents: number | null
  updatedBy: string | null
  updatedAt: string | null
  bookedMovementId: number | null
  /**
   * For a delivery created by an API client (`createdBy` = `api-client:<id>`): that client's name.
   * Null for human-created deliveries, and for a client that no longer exists — show `createdBy`.
   */
  createdByClientName?: string | null
}

/**
 * What every mutation returns: the document plus the pre-orders the action left uncovered
 * (booking short, reducing, cancelling). The list is informational — the action has already
 * completed by the time the client sees it.
 */
export interface DeliveryResult {
  delivery: DeliveryDetail
  affectedOrders: Array<AffectedOrder>
}

export interface DeliveryWriteLine {
  sku: string
  expectedQty: number
  note?: string | null
  /**
   * Optional net purchase price per PIECE, in cents — hand-announced deliveries only. The server
   * refuses it on an imported delivery (`409 imported_price_readonly`); omit it there.
   */
  unitCostCents?: number | null
}

export interface DeliveryWriteBody {
  supplier: string
  deliveryNoteRef?: string | null
  expectedDate: string
  countsForPreorders: boolean
  note?: string | null
  lines: Array<DeliveryWriteLine>
}

export interface DeliveryBookLine {
  sku: string
  receivedQty: number
  note?: string | null
  /**
   * Net purchase price per PIECE, in cents, of a hand-announced delivery's position; null books it
   * without a price. Omit it on an imported delivery (`409 imported_price_readonly`).
   */
  unitCostCents?: number | null
}

/**
 * The last booked net purchase price per piece of one article at the store: from the most recent
 * BOOKED delivery position carrying a price, imported or manual.
 */
export interface DeliveryLastPrice {
  sku: string
  /** Net, per piece, rounded to whole cents. */
  unitCostCents: number
  /** ISO timestamp of the booking. */
  bookedAt: string
  deliveryId: string
}

export interface DeliveryBookBody {
  note?: string | null
  lines: Array<DeliveryBookLine>
}

const base = (restaurantId: number) =>
  `/api/v0/admin/inventory/${restaurantId}/deliveries`

export const inventoryDeliveriesApi = {
  list: (
    restaurantId: number,
    params: {
      /** Repeated `status` parameters; omit for every status. */
      status?: Array<DeliveryListStatus>
      search?: string
      offset?: number
      limit?: number
    } = {},
  ) => {
    const sp = new URLSearchParams()
    for (const s of params.status ?? []) sp.append('status', s)
    if (params.search) sp.set('search', params.search)
    if (params.offset != null) sp.set('offset', String(params.offset))
    if (params.limit != null) sp.set('limit', String(params.limit))
    const query = sp.toString()
    return apiRequest<{ items: Array<DeliveryListItem>; total: number }>(
      query ? `${base(restaurantId)}?${query}` : base(restaurantId),
    )
  },

  /** SKUs without a booked price are absent from `items`. */
  lastPrices: (restaurantId: number, skus: Array<string>) => {
    const sp = new URLSearchParams()
    for (const sku of skus) sp.append('sku', sku)
    return apiRequest<{ items: Array<DeliveryLastPrice> }>(
      `${base(restaurantId)}/last-prices?${sp.toString()}`,
    )
  },

  get: (restaurantId: number, id: string) =>
    apiRequest<DeliveryDetail>(`${base(restaurantId)}/${id}`),

  create: (restaurantId: number, body: DeliveryWriteBody) =>
    apiRequest<DeliveryResult>(base(restaurantId), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (restaurantId: number, id: string, body: DeliveryWriteBody) =>
    apiRequest<DeliveryResult>(`${base(restaurantId)}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  book: (restaurantId: number, id: string, body: DeliveryBookBody) =>
    apiRequest<DeliveryResult>(`${base(restaurantId)}/${id}/book`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  cancel: (restaurantId: number, id: string) =>
    apiRequest<DeliveryResult>(`${base(restaurantId)}/${id}/cancel`, {
      method: 'POST',
    }),
}
